import { useEffect, useRef, useState } from "react";
import { addDaysIso, formatDueLabel, nextWeekStartIso, todayIso, weekendStartIso } from "@/lib/dates";
import type { PopoverAlign } from "@/lib/popoverAlign";
import AnchoredPopover from "@/components/AnchoredPopover";

const POPOVER_WIDTH = 224;

interface Props {
  value: string | null;
  onChange: (next: string | null) => void;
  onOpenChange?: (open: boolean) => void;
  iconOnly?: boolean;
  popoverAlign?: PopoverAlign;
}

export default function QuickDuePicker({ value, onChange, onOpenChange, iconOnly = false, popoverAlign = "start" }: Props) {
  const [open, setOpen] = useState(false);
  const [dateDraft, setDateDraft] = useState(value ?? "");
  const wrapRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setDateDraft(value ?? "");
  }, [value]);

  // Notify parent on real open/close transitions; skip mount.
  const onOpenChangeRef = useRef(onOpenChange);
  useEffect(() => {
    onOpenChangeRef.current = onOpenChange;
  });
  const mountedRef = useRef(false);
  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      return;
    }
    onOpenChangeRef.current?.(open);
  }, [open]);

  // onBlur on the date input doesn't fire when the popover unmounts on
  // outside-click, so the draft would otherwise be silently dropped.
  const commitRef = useRef<() => void>(() => {});
  commitRef.current = () => {
    const next = dateDraft || null;
    if (next === (value ?? null)) return;
    onChange(next);
  };

  useEffect(() => {
    if (!open) return;
    function handle(e: MouseEvent) {
      const target = e.target as Node;
      if (wrapRef.current && !wrapRef.current.contains(target) && !popoverRef.current?.contains(target)) {
        commitRef.current();
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setDateDraft(value ?? "");
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handle);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", handle);
      document.removeEventListener("keydown", onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, value]);

  function pick(next: string | null) {
    onChange(next);
    setOpen(false);
  }

  function commitDateDraft() {
    commitRef.current();
  }

  const label = value ? formatDueLabel(value).text : null;
  const isSet = value !== null;

  return (
    <div className="relative inline-flex items-center" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`quick-meta-trigger ${isSet ? "quick-meta-trigger-set" : ""}`}
        aria-label={isSet ? `Срок: ${label}` : "Указать срок"}
        title={isSet ? `Срок: ${label}` : "Указать срок"}
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
        {label && !iconOnly && <span className="quick-meta-label">{label}</span>}
      </button>
      {open && (
        <AnchoredPopover ref={popoverRef} anchorRef={wrapRef} width={POPOVER_WIDTH} align={popoverAlign} testId="quick-due-popover">
          <button type="button" onClick={() => pick(todayIso())} className="details-popover-item">
            Сегодня
          </button>
          <button type="button" onClick={() => pick(addDaysIso(todayIso(), 1))} className="details-popover-item">
            Завтра
          </button>
          <button type="button" onClick={() => pick(weekendStartIso())} className="details-popover-item">
            Выходные
          </button>
          <button type="button" onClick={() => pick(nextWeekStartIso())} className="details-popover-item">
            Следующая неделя
          </button>
          <input
            type="date"
            value={dateDraft}
            onChange={(e) => setDateDraft(e.target.value)}
            onBlur={commitDateDraft}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                pick(dateDraft || null);
              }
            }}
            className="details-popover-search"
            aria-label="Указать дату"
          />
          {value && (
            <button
              type="button"
              onClick={() => pick(null)}
              className="details-popover-item text-red-600 dark:text-red-400"
            >
              Убрать
            </button>
          )}
        </AnchoredPopover>
      )}
    </div>
  );
}
